import {
	AdditionalQuestionData,
	additionalQuestionIndexQueryKey,
	AdditionalQuestionIndexQueryResponse,
	additionalQuestionUpdate,
	CreateAdditionalQuestionData,
	UpdateAdditionalQuestionData,
	useAdditionalQuestionDestroy,
	useAdditionalQuestionIndex,
	useAdditionalQuestionStore,
	useAdditionalQuestionUpdate,
} from '@jobilla/nexus-sdk'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { logger } from '../../ui'

export type UseAdditionalQuestionsArgs = {
	formId?: string
}

export type UseAdditionalQuestionsReturns = {
	questions: AdditionalQuestionData[]
	isLoading: boolean
	isSaving: boolean
	create: (data: CreateAdditionalQuestionData) => void
	update: (questionId: string, data: UpdateAdditionalQuestionData) => void
	updateLocal: (questionId: string, data: UpdateAdditionalQuestionData) => void
	remove: (questionId: string) => void
	reorder: (fromIndex: number, toIndex: number) => void
}

export function useAdditionalQuestions({ formId }: UseAdditionalQuestionsArgs): UseAdditionalQuestionsReturns {
	const queryClient = useQueryClient()

	const { data, isLoading } = useAdditionalQuestionIndex(formId ?? '', {
		enabled: !!formId,
	})

	const questions = useMemo(() => data ?? [], [data])

	const queryKey = formId ? additionalQuestionIndexQueryKey(formId) : undefined

	const updateMutation = useAdditionalQuestionUpdate({
		onMutate: async ({ question: questionId, data: updateData }) => {
			if (!queryKey) { return }

			await queryClient.cancelQueries({ queryKey })
			const previousData = queryClient.getQueryData<AdditionalQuestionIndexQueryResponse>(queryKey)

			queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, (old) => {
				if (!old) { return undefined }
				return old.map((q) => q.id === questionId ? { ...q, ...updateData } as AdditionalQuestionData : q)
			})

			return { previousData }
		},
		onError: (err, variables, context) => {
			if (queryKey && context?.previousData) {
				queryClient.setQueryData(queryKey, context.previousData)
			}
			logger.error(err, {
				category: 'additional_questions',
				tags: { action: 'update' },
				data: { formId, questionId: variables.question },
			})
		},
		onSettled: () => {
			if (!queryKey) { return }
			queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
		},
	})

	const createMutation = useAdditionalQuestionStore({
		onSuccess: (response) => {
			if (!queryKey) { return }
			queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, (old) => {
				if (!old) { return [response] }
				return [...old, response]
			})
		},
		onError: (err, variables) => {
			logger.error(err, {
				category: 'additional_questions',
				tags: { action: 'create' },
				data: { formId, variables },
			})
		},
		onSettled: () => {
			if (!queryKey) { return }
			queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
		},
	})

	const deleteMutation = useAdditionalQuestionDestroy({
		onMutate: async ({ question: questionId }) => {
			if (!queryKey) { return }

			await queryClient.cancelQueries({ queryKey })
			const previousData = queryClient.getQueryData<AdditionalQuestionIndexQueryResponse>(queryKey)

			queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, (old) => {
				if (!old) { return undefined }
				return old.filter((q) => q.id !== questionId)
			})

			return { previousData }
		},
		onError: (err, variables, context) => {
			if (queryKey && context?.previousData) {
				queryClient.setQueryData(queryKey, context.previousData)
			}
			logger.error(err, {
				category: 'additional_questions',
				tags: { action: 'delete' },
				data: { formId, questionId: variables.question },
			})
		},
		onSettled: () => {
			if (!queryKey) { return }
			queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
		},
	})

	const updateLocal = useCallback((questionId: string, updateData: UpdateAdditionalQuestionData) => {
		if (!queryKey) { return }
		queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, (old) => {
			if (!old) { return undefined }
			return old.map((q) => q.id === questionId ? { ...q, ...updateData } as AdditionalQuestionData : q)
		})
	}, [queryKey, queryClient])

	const update = useCallback((questionId: string, updateData: UpdateAdditionalQuestionData) => {
		if (!formId) { return }
		updateMutation.mutate({ question: questionId, data: updateData })
	}, [formId, updateMutation])

	const create = useCallback((createData: CreateAdditionalQuestionData) => {
		if (!formId) { return }
		createMutation.mutate({ form: formId!, data: createData })
	}, [formId, createMutation])

	const remove = useCallback((questionId: string) => {
		if (!formId) { return }
		deleteMutation.mutate({ question: questionId })
	}, [formId, deleteMutation])

	const reorderMutation = useMutation({
		mutationFn: async (
			{ fromIndex, toIndex, originalQuestions }: {
				fromIndex: number
				toIndex: number
				originalQuestions: AdditionalQuestionData[]
			},
		) => {
			const reordered = [...originalQuestions]
			if (fromIndex < 0 || fromIndex >= reordered.length || toIndex < 0 || toIndex >= reordered.length) { return }
			const [moved] = reordered.splice(fromIndex, 1)
			if (!moved) { return }
			reordered.splice(toIndex, 0, moved)

			const withPositions = reordered.map((q, i) => ({ ...q, position: i }))
			const changedPositions = withPositions.filter((q) => {
				const original = originalQuestions.find((oq) => oq.id === q.id)
				return original && original.position !== q.position
			})

			await Promise.all(
				changedPositions.map((q) => additionalQuestionUpdate(q.id, { position: q.position })),
			)
		},
		onMutate: async () => {
			if (!queryKey) { return }
			await queryClient.cancelQueries({ queryKey })
		},
		onError: (err, variables) => {
			if (queryKey && variables.originalQuestions) {
				queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, variables.originalQuestions)
			}
			logger.error(err, {
				category: 'additional_questions',
				tags: { action: 'reorder' },
				data: { formId },
			})
		},
		onSettled: () => {
			if (!queryKey) { return }
			queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
		},
	})

	const reorder = useCallback((fromIndex: number, toIndex: number) => {
		if (!formId || !queryKey) { return }
		const originalQuestions = queryClient.getQueryData<AdditionalQuestionIndexQueryResponse>(queryKey) ?? []

		const reordered = [...originalQuestions]
		if (fromIndex < 0 || fromIndex >= reordered.length || toIndex < 0 || toIndex >= reordered.length) { return }
		const [moved] = reordered.splice(fromIndex, 1)
		if (!moved) { return }
		reordered.splice(toIndex, 0, moved)
		const withPositions = reordered.map((q, i) => ({ ...q, position: i }))
		queryClient.setQueryData<AdditionalQuestionIndexQueryResponse>(queryKey, withPositions)

		reorderMutation.mutate({ fromIndex, toIndex, originalQuestions })
	}, [formId, queryKey, queryClient, reorderMutation])

	return {
		questions,
		isLoading,
		isSaving: updateMutation.isPending || createMutation.isPending || deleteMutation.isPending
			|| reorderMutation.isPending,
		create,
		update,
		updateLocal,
		remove,
		reorder,
	}
}
